// ============================================================
// ARCHIVO: src/components/TabDocumentos.jsx
// Validación de documentos de inscripción PNA (y futuro TUM-B)
// Usa: GET /progreso/admin/programas/:code/docs
//      PATCH /progreso/admin/docs/:docId/review
// ============================================================
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Stack, TextField, MenuItem,
  Chip, Alert, CircularProgress, Divider, IconButton,
  Tooltip, TablePagination, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  FiRefreshCw, FiSearch, FiExternalLink,
  FiCheckCircle, FiXCircle, FiMessageSquare,
} from "react-icons/fi";
import { getAuth } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";

const COLORS = {
  bg: "#f5f0ff", white: "#ffffff", whiteSoft: "#fff8ff",
  subtle: "#e6dfef", red: "#ff3333", redDark: "#cc0000",
  textMain: "#2d233a", textMuted: "#6c6478",
  green: "#1b5e20",  greenBg: "#e8f5e9",
  orange: "#e65100", orangeBg: "#fff3e0",
  danger: "#b00020", dangerBg: "#ffebee",
  blue: "#0d47a1",   blueBg: "#e3f2fd",
};

// ─── helpers ─────────────────────────────────────────────────
function safeStr(v) { return v === null || v === undefined ? "" : String(v); }
function fullName(r) {
  return `${safeStr(r?.nombre)} ${safeStr(r?.apellido_pat)} ${safeStr(r?.apellido_mat)}`
    .replace(/\s+/g, " ").trim();
}
function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("es-MX", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return safeStr(s); }
}


async function authedFetch(url, options = {}) {
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("No autenticado");
    const token = await user.getIdToken();
    const res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        "x-firebase-uid": user.uid,
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `HTTP ${res.status}`);
    }
    return res.json();
  } catch (err) {
    if (err.message === "Failed to fetch") {
      throw new Error("Sin conexión con el servidor. Intenta de nuevo en unos segundos.");
    }
    throw err;
  }
}

// ─── Chip de estado ───────────────────────────────────────────
function DocStatusChip({ status }) {
  const map = {
    submitted: { label: "Pendiente", bg: COLORS.orangeBg, color: COLORS.orange },
    approved:  { label: "Validado",  bg: COLORS.greenBg,  color: COLORS.green  },
    rejected:  { label: "Inactivo",  bg: COLORS.dangerBg, color: COLORS.danger },
  };
  const s = map[safeStr(status)] || { label: "—", bg: COLORS.subtle, color: COLORS.textMuted };
  return (
    <Chip label={s.label} size="small"
      sx={{ fontSize: 11, height: 22, fontWeight: 700,
        backgroundColor: s.bg, color: s.color, border: "none" }} />
  );
}

// ─── Modal de rechazo ─────────────────────────────────────────
function ModalRechazo({ open, doc, onClose, onConfirm, saving }) {
  const [nota, setNota] = useState("");
  useEffect(() => { if (open) setNota(""); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 900, color: COLORS.textMain, pb: 0.5 }}>
        Inactivar documento
      </DialogTitle>
      <DialogContent>
        {doc && (
          <Typography sx={{ fontSize: 13, color: COLORS.textMuted, mb: 1.5 }}>
            <strong>{fullName(doc)}</strong> — {safeStr(doc.document_title || doc.activity_name)}
          </Typography>
        )}
        <TextField
          label="Motivo de inactivación *" multiline rows={3} fullWidth
          value={nota} onChange={(e) => setNota(e.target.value)}
          placeholder="Describe al aspirante por qué se inactivó el documento y qué debe corregir..."
          helperText="Este mensaje será visible para el aspirante al ver su actividad."
          sx={{ mt: 0.5 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}
          sx={{ textTransform: "none", color: COLORS.textMuted }}>
          Cancelar
        </Button>
        <Button variant="contained" disabled={saving || !nota.trim()}
          onClick={() => onConfirm(nota.trim())}
          sx={{ backgroundColor: COLORS.danger, "&:hover": { backgroundColor: "#8b0000" },
            textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
          {saving && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
          Confirmar inactivación
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Tarjeta de documento ─────────────────────────────────────
function DocCard({ doc, onApprove, onReject, savingId }) {
  const isSaving  = savingId === doc.doc_id;
  const isPending  = doc.doc_status === "submitted";
  const isApproved = doc.doc_status === "approved";
  const isRejected = doc.doc_status === "rejected";

  return (
    <Box sx={{
      p: 2, borderRadius: 2, mb: 1.5,
      border: `1px solid ${isApproved ? "#a5d6a7" : isRejected ? "#ef9a9a" : COLORS.subtle}`,
      backgroundColor: isApproved ? "#f1f8f1" : isRejected ? "#fff5f5" : COLORS.white,
    }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>

        {/* ── Info aspirante ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
            <Typography sx={{ fontWeight: 800, fontSize: 14, color: COLORS.textMain }}>
              {fullName(doc)}
            </Typography>
            <Chip label={safeStr(doc.matricula)} size="small"
              sx={{ fontSize: 10, height: 18, backgroundColor: COLORS.subtle,
                color: COLORS.textMuted, fontWeight: 700 }} />
            {doc.estado && (
              <Chip label={safeStr(doc.estado)} size="small"
                sx={{ fontSize: 10, height: 18, backgroundColor: COLORS.blueBg,
                  color: COLORS.blue, fontWeight: 600 }} />
            )}
          </Stack>

          <Typography sx={{ fontSize: 12, color: COLORS.textMuted, mb: 0.3 }}>
            <strong>Documento:</strong> {safeStr(doc.document_title || doc.activity_name || "—")}
          </Typography>

          <Typography sx={{ fontSize: 12, color: COLORS.textMuted, mb: 0.3 }}>
            <strong>Subido:</strong> {fmtDate(doc.created_at)}
            {doc.updated_at && doc.updated_at !== doc.created_at && (
              <span> · <strong>Actualizado:</strong> {fmtDate(doc.updated_at)}</span>
            )}
          </Typography>

          {/* Motivo de rechazo */}
          {isRejected && doc.review_note && (
            <Alert severity="error" icon={<FiMessageSquare size={14} />}
              sx={{ mt: 1, borderRadius: 2, fontSize: 12, py: 0.3, px: 1.5 }}>
              <strong>Motivo:</strong> {safeStr(doc.review_note)}
            </Alert>
          )}

          {(isApproved || isRejected) && doc.reviewed_at && (
            <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mt: 0.5 }}>
              Revisado el {fmtDate(doc.reviewed_at)}
            </Typography>
          )}
        </Box>

        {/* ── Acciones ── */}
        <Stack spacing={1} alignItems={{ xs: "flex-start", md: "flex-end" }}
          sx={{ minWidth: 190, flexShrink: 0 }}>

          <DocStatusChip status={doc.doc_status} />

          {/* Ver archivo */}
          <Button size="small" variant="outlined"
            startIcon={<FiExternalLink size={12} />}
            href={doc.file_url} target="_blank" rel="noopener noreferrer"
            sx={{ borderRadius: 999, textTransform: "none", fontSize: 11,
              borderColor: COLORS.subtle, color: COLORS.textMain, whiteSpace: "nowrap",
              "&:hover": { borderColor: COLORS.blue, color: COLORS.blue } }}>
            {safeStr(doc.file_name || "Ver archivo").slice(0, 25) +
              (safeStr(doc.file_name).length > 25 ? "…" : "")}
          </Button>

          {/* Aprobar / Inactivar — solo si pendiente */}
          {isPending && (
            <Stack direction="row" spacing={0.8}>
              <Button size="small" variant="contained" disabled={isSaving}
                startIcon={isSaving
                  ? <CircularProgress size={12} sx={{ color: "#fff" }} />
                  : <FiCheckCircle size={12} />}
                onClick={() => onApprove(doc)}
                sx={{ borderRadius: 999, textTransform: "none", fontSize: 12,
                  fontWeight: 700, backgroundColor: COLORS.green,
                  "&:hover": { backgroundColor: "#145214" },
                  "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" } }}>
                Aprobar
              </Button>
              <Button size="small" variant="outlined" disabled={isSaving}
                startIcon={<FiXCircle size={12} />}
                onClick={() => onReject(doc)}
                sx={{ borderRadius: 999, textTransform: "none", fontSize: 12,
                  fontWeight: 700, borderColor: COLORS.danger, color: COLORS.danger,
                  "&:hover": { backgroundColor: COLORS.dangerBg } }}>
                Inactivar
              </Button>
            </Stack>
          )}

          {isApproved && (
            <Typography sx={{ fontSize: 11, color: COLORS.green, fontWeight: 600 }}>
              ✓ Documento validado
            </Typography>
          )}
          {isRejected && (
            <Typography sx={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>
              El aspirante puede volver a subir
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────
export default function TabDocumentos() {
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [counts, setCounts]     = useState({});
  const [totalDocs, setTotalDocs] = useState(0);

  // Filtros
  const [programa, setPrograma]         = useState("INSC_PNA");
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [matricula, setMatricula]       = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(20);
  const [searchDebounced, setSearchDebounced] = useState("");

  // Meta (scope del moderador)
  const [programas, setProgramas]             = useState([]);
  const [estadosScope, setEstadosScope]       = useState([]);
  const [groupCodesScope, setGroupCodesScope] = useState([]);

  // Acciones
  const [savingId, setSavingId]         = useState(null);
  const [snack, setSnack]               = useState({ open: false, msg: "", severity: "success" });
  const [modalRechazo, setModalRechazo] = useState({ open: false, doc: null });

  // ── Cargar meta ─────────────────────────────────────────────
  useEffect(() => {
    authedFetch(`${API_BASE}/progreso/admin/evaluar/programas`)
      .then(d => {
        const progs = Array.isArray(d?.programs) ? d.programs : [];
        setProgramas(progs);
        setEstadosScope(Array.isArray(d?.estados) ? d.estados : []);
        setGroupCodesScope(Array.isArray(d?.groupCodes) ? d.groupCodes : []);
        // Asegurar que el programa seleccionado esté en la lista
        const inscProgs = progs.filter(p => p.code?.startsWith("INSC_"));
        if (inscProgs.length > 0) {
          const pna = inscProgs.find(p => p.code === "INSC_PNA");
          setPrograma(pna ? pna.code : inscProgs[0].code);
        }
      })
      .catch(() => {});
  }, []);

  // ── Debounce de la búsqueda ────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(matricula.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [matricula]);

  // ── Cargar docs del programa ─────────────────────────────────
  const fetchDocs = useCallback(async () => {
    if (!programa) return;
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE}/progreso/admin/programas/${programa}/docs`);
      if (statusFilter) url.searchParams.set("status", statusFilter);
      url.searchParams.set("page", page + 1);
      url.searchParams.set("limit", rowsPerPage);
      if (searchDebounced) url.searchParams.set("search", searchDebounced);

      const data = await authedFetch(url.toString());
      setDocs(Array.isArray(data.docs) ? data.docs : []);
      setCounts(data.counts || {});
      setTotalDocs(data.pagination?.total || 0);
    } catch (e) {
      setError(e.message);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [programa, statusFilter, page, rowsPerPage, searchDebounced]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // ── Filtrado local (solo estado) ─────────────────────────────
  const docsFiltrados = docs.filter(d => {
    if (estadoFilter && d.estado !== estadoFilter) return false;
    return true;
  });

  // ── Aprobar ──────────────────────────────────────────────────
  const handleApprove = async (doc) => {
    setSavingId(doc.doc_id);
    try {
      await authedFetch(`${API_BASE}/progreso/admin/docs/${doc.doc_id}/review`, {
        method: "PATCH",
        body: JSON.stringify({ status: "approved", review_note: null }),
      });
      setDocs(prev => prev.map(d =>
        d.doc_id === doc.doc_id
          ? { ...d, doc_status: "approved", reviewed_at: new Date().toISOString() }
          : d
      ));
      setCounts(prev => ({
        ...prev,
        submitted: Math.max(0, (prev.submitted || 0) - 1),
        approved: (prev.approved || 0) + 1,
      }));
      setSnack({ open: true, msg: "Documento aprobado. La actividad del aspirante queda completada.", severity: "success" });
    } catch (e) {
      setSnack({ open: true, msg: `Error: ${e.message}`, severity: "error" });
    } finally {
      setSavingId(null);
    }
  };

  // ── Rechazar ─────────────────────────────────────────────────
  const handleReject   = (doc) => setModalRechazo({ open: true, doc });

  const handleConfirmRechazo = async (nota) => {
    const doc = modalRechazo.doc;
    setSavingId(doc.doc_id);
    setModalRechazo({ open: false, doc: null });
    try {
      await authedFetch(`${API_BASE}/progreso/admin/docs/${doc.doc_id}/review`, {
        method: "PATCH",
        body: JSON.stringify({ status: "rejected", review_note: nota }),
      });
      setDocs(prev => prev.map(d =>
        d.doc_id === doc.doc_id
          ? { ...d, doc_status: "rejected", review_note: nota, reviewed_at: new Date().toISOString() }
          : d
      ));
      setCounts(prev => ({
        ...prev,
        submitted: Math.max(0, (prev.submitted || 0) - 1),
        rejected: (prev.rejected || 0) + 1,
      }));
      setSnack({ open: true, msg: "Documento inactivado. El aspirante podrá volver a subir.", severity: "warning" });
    } catch (e) {
      setSnack({ open: true, msg: `Error: ${e.message}`, severity: "error" });
    } finally {
      setSavingId(null);
    }
  };

  const estadoOpciones = groupCodesScope.length > 0 ? groupCodesScope : estadosScope;

  // Programas de inscripción disponibles (filtrar del scope)
  const programasInscripcion = programas.filter(p =>
    p.code?.startsWith("INSC_") || ["INSC_PNA", "INSC_TUMB"].includes(p.code)
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.subtle}`, mb: 2 }}>

        {/* ── Filtros ── */}
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.2} alignItems="center" sx={{ mb: 1.5 }}>

          <TextField select size="small" label="Programa" value={programa}
            onChange={e => { setPrograma(e.target.value); setPage(0); }}
            sx={{ width: 230, backgroundColor: COLORS.white }}>
            {programasInscripcion.length > 0
              ? programasInscripcion.map(p => (
                  <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>
                ))
              : <MenuItem value="INSC_PNA">Inscripción PNA (INSC_PNA)</MenuItem>
            }
          </TextField>

          <TextField select size="small" label="Estado del documento" value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ width: 210, backgroundColor: COLORS.white }}>
            <MenuItem value="">Todos los estados</MenuItem>
            <MenuItem value="submitted">
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.orange }} />
                Pendientes ({counts.submitted || 0})
              </Stack>
            </MenuItem>
            <MenuItem value="approved">
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.green }} />
                Validados ({counts.approved || 0})
              </Stack>
            </MenuItem>
            <MenuItem value="rejected">
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.danger }} />
                Inactivos ({counts.rejected || 0})
              </Stack>
            </MenuItem>
          </TextField>

          {estadoOpciones.length > 0 && (
            <TextField select size="small"
              label={groupCodesScope.length > 0 ? "Grupo" : "Estado"}
              value={estadoFilter}
              onChange={e => { setEstadoFilter(e.target.value); setPage(0); }}
              sx={{ width: 200, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todos</MenuItem>
              {estadoOpciones.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </TextField>
          )}

          <TextField size="small" label="Matrícula o nombre" value={matricula}
            onChange={e => setMatricula(e.target.value)}
            sx={{ width: 190, backgroundColor: COLORS.white }}
            InputProps={{ endAdornment:
              <Tooltip title="Filtrar">
                <IconButton size="small"><FiSearch size={14} /></IconButton>
              </Tooltip>
            }}
          />

          <Tooltip title="Refrescar">
            <IconButton onClick={fetchDocs}
              sx={{ border: `1px solid ${COLORS.subtle}`, borderRadius: 2,
                backgroundColor: COLORS.white }}>
              <FiRefreshCw size={16} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* ── Resumen contadores ── */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
          {[
            { label: "Pendientes",  key: "submitted", bg: COLORS.orangeBg, color: COLORS.orange },
            { label: "Validados",   key: "approved",  bg: COLORS.greenBg,  color: COLORS.green  },
            { label: "Inactivos",   key: "rejected",  bg: COLORS.dangerBg, color: COLORS.danger },
          ].map(c => (
            <Chip key={c.key} size="small"
              label={`${c.label}: ${counts[c.key] || 0}`}
              onClick={() => { setStatusFilter(c.key); setPage(0); }}
              sx={{ fontWeight: 700, fontSize: 12, cursor: "pointer",
                backgroundColor: statusFilter === c.key ? c.color : c.bg,
                color: statusFilter === c.key ? "#fff" : c.color,
                border: "none",
                "&:hover": { backgroundColor: c.color, color: "#fff" } }} />
          ))}
          <Chip size="small" label={`Total: ${totalDocs}`}
            sx={{ fontWeight: 600, fontSize: 12, backgroundColor: COLORS.subtle,
              color: COLORS.textMuted, border: "none" }} />
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>}
        <Divider sx={{ mb: 2, borderColor: COLORS.subtle }} />

        {/* ── Lista ── */}
        {loading ? (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={20} />
            <Typography sx={{ color: COLORS.textMuted }}>Cargando documentos…</Typography>
          </Stack>
        ) : docsFiltrados.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 14 }}>
              {docs.length === 0
                ? "No hay documentos registrados en este programa aún."
                : "Sin documentos con los filtros actuales."}
            </Typography>
          </Box>
        ) : (
          <>
            {docsFiltrados.map(doc => (
              <DocCard
                key={doc.doc_id}
                doc={doc}
                onApprove={handleApprove}
                onReject={handleReject}
                savingId={savingId}
              />
            ))}
            <TablePagination
              component="div"
              count={totalDocs}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Por página:"
            />
          </>
        )}
      </Paper>

      {/* ── Modal rechazo ── */}
      <ModalRechazo
        open={modalRechazo.open}
        doc={modalRechazo.doc}
        onClose={() => setModalRechazo({ open: false, doc: null })}
        onConfirm={handleConfirmRechazo}
        saving={!!savingId}
      />

      {/* ── Snackbar ── */}
      {snack.open && (
        <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 9999, minWidth: 320 }}>
          <Alert severity={snack.severity}
            onClose={() => setSnack(s => ({ ...s, open: false }))}
            sx={{ borderRadius: 2, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>
            {snack.msg}
          </Alert>
        </Box>
      )}
    </Box>
  );
}